import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from 'antd';
import { IRating } from 'src/common-types/index';
import { DATE_FORMAT_CHECK_IN_OUT_YEAR, MINIMUM_RATING_TO_SHOW } from 'src/constants/index';
import {
  creatingArrayWithNumber,
  formatDateCheckInOut,
  getCondoTextRating,
  getReserveUnitUrl,
  renderNumber,
} from 'src/utils/index';
import { ManDarkSvg, NoImageSvg, StarSvg } from 'src/assets/index';
import { BlueButton } from 'src/components/components/index';
import './style.scss';

interface IProps {
  imageUrl: string;
  title: string;
  address: string;
  unitName: string;
  unitDescription?: string;
  maxOccupancy?: number;
  adultsCount: number;
  childrenCount: number;
  bedroomsCount: number;
  bathroomsCount: number;
  checkIn: string;
  checkInTime?: string;
  checkOut: string;
  checkOutTime?: string;
  nightsCount: number;
  condoId?: number;
  unitId?: string;
  price?: number;
  isNotification?: boolean;
  bookingComplete?: boolean;
  isModalInfo?: boolean;
  rating?: IRating;
  supplierIdentifier?: string;
}

const ZERO = 0;
const ONE = 1;

export class UnitMainInfo extends React.Component<IProps, null> {
  render(): React.ReactNode {
    const {
      imageUrl,
      title,
      address,
      unitName,
      unitDescription,
      maxOccupancy,
      checkIn,
      checkOut,
      nightsCount,
      condoId,
      unitId,
      price,
      checkInTime,
      checkOutTime,
      isModalInfo = false,
      rating,
      supplierIdentifier,
    } = this.props;
    const showRating = rating && rating?.value > ZERO;

    return (
      <div className="unit-main-info">
        {imageUrl.length !== ZERO ? (
          <div className="unit-main-info__unit-image print">
            <img className="unit-main-info__unit-img print" src={imageUrl} alt={unitName} />
          </div>
        ) : (
          <div className="unit-info__unit-img no-image">
            <NoImageSvg />
          </div>
        )}
        <div className="unit-main-info__content border-content">
          <p className="unit-main-info__supplier">{supplierIdentifier}</p>
          <p className="unit-main-info__condo-name">{title}</p>
          <p className="unit-main-info__address">{address}</p>
          {!isModalInfo ? (
            <div className="unit-main-info__link-reviews">
              {showRating ? <StarSvg /> : null}
              <p className="unit-main-info__trust-you-text bold">
                {showRating ? renderNumber(rating.value, ONE) : ''}
              </p>
              {rating.reviewsCount ? (
                <p className="unit-main-info__trust-you-text">
                  ({renderNumber(rating.reviewsCount)} <FormattedMessage id="reviews" />)
                </p>
              ) : showRating && rating.value >= MINIMUM_RATING_TO_SHOW ? (
                <p className="unit-main-info__trust-you-text">
                  <FormattedMessage
                    id={getCondoTextRating(Number(renderNumber(rating.value, ONE)))}
                  />
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="unit-main-info__unit-info">
            <div className="unit-main-info__unit-name-wrapper">
              <Tooltip title={unitName} placement="topLeft">
                <p className="unit-main-info__unit-name capitalize">{unitName}</p>
              </Tooltip>
              {maxOccupancy > ZERO && (
                <div className="unit-main-info__guests">
                  {creatingArrayWithNumber(maxOccupancy, ONE).map((value, i) => {
                    return (
                      <span className="unit-main-info__icon-wrapper" key={i}>
                        <ManDarkSvg />
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="unit-main-info__unit-info-description lowercase">{unitDescription}</p>
          </div>
          {checkIn && checkOut && (
            <div className="unit-main-info__period">
              <div className="unit-main-info__period-item">
                <p className="unit-main-info__period-item-title">
                  <FormattedMessage id="check.in" />:
                </p>
                <p className="unit-main-info__period-item-description">
                  {formatDateCheckInOut(checkIn, DATE_FORMAT_CHECK_IN_OUT_YEAR)}
                  {checkInTime ? (
                    <>
                      {', '}
                      {checkInTime}
                    </>
                  ) : null}
                </p>
              </div>
              <div className="unit-main-info__period-item">
                <p className="unit-main-info__period-item-title">
                  <FormattedMessage id="check.out" />:
                </p>
                <p className="unit-main-info__period-item-description">
                  {formatDateCheckInOut(checkOut, DATE_FORMAT_CHECK_IN_OUT_YEAR)}
                  {checkOutTime ? (
                    <>
                      {', '}
                      <FormattedMessage id="till" /> {checkOutTime}
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          )}
          {price !== ZERO && isModalInfo && (
            <div className="unit-main-info__price-block">
              <div className="unit-main-info__price-total">
                <div className="unit-main-info__refundable">
                  <div className="unit-main-info__refundable-nonref">
                    <span>
                      <FormattedMessage id="non.refundable" />
                    </span>
                  </div>
                </div>
                <p className="unit-main-info__price-total-text">
                  ${renderNumber(Math.floor(price))}
                </p>
                <div className="unit-main-info__for-info">
                  <p className="unit-main-info__for-info-text">
                    <FormattedMessage id="for" />{' '}
                    <FormattedMessage id="nights" values={{ count: nightsCount }} />
                  </p>
                  <span className="unit-main-info__price-includes-taxes">
                    <FormattedMessage id="abbreviated.price.includes.taxes" />
                  </span>
                </div>
              </div>
              <div className="unit-main-info__action">
                <Link to={getReserveUnitUrl(condoId, unitId)}>
                  <BlueButton>
                    <FormattedMessage id="reserve" />
                  </BlueButton>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
